/** Host that serves PAI courses from the pai-production branch. */
const PAI_ORIGIN = 'https://pai.stanford.edu';

/** Host whose bundle ships from the dev line and must not serve PAI courses. */
const CODE_IN_PLACE_HOSTNAME = 'codeinplace.stanford.edu';

/**
 * Path prefixes that belong to PAI. Mirrors the `codeinplace-production`
 * hosting `redirects` in the repo-root `firebase.json`, which only fire on a
 * full page load; keep the two lists in sync.
 */
const PAI_PATH_PREFIXES = ['/apply/pai1', '/pai', '/pai1'];

/**
 * Returns the pai.stanford.edu URL that a PAI page opened on
 * codeinplace.stanford.edu should be sent to, keeping path, query and hash.
 *
 * A prefix matches whole path segments only: `/pai1` and `/pai1/home` match
 * the `/pai1` prefix, `/pai10` does not.
 *
 * @param location The current page location.
 * @param location.hostname Host the app is served from.
 * @param location.pathname URL path, starting with `/`.
 * @param location.search Query string including its leading `?`, or empty.
 * @param location.hash Fragment including its leading `#`, or empty.
 * @return The absolute pai.stanford.edu URL, or `null` when the host is not
 *     codeinplace.stanford.edu or the path is not a PAI path.
 * @throws Never.
 */
export function getPaiHostRedirectUrl({
  hostname,
  pathname,
  search,
  hash,
}: {
  hostname: string;
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  if (hostname !== CODE_IN_PLACE_HOSTNAME) {
    return null;
  }
  const isPaiPath = PAI_PATH_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  if (!isPaiPath) {
    return null;
  }
  return `${PAI_ORIGIN}${pathname}${search}${hash}`;
}
